import type { KeyboardEvent } from 'react';

interface RovingFocus {
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

/**
 * Vertical roving-focus keyboard handler, modeled on `layout/tab-bar.tsx`'s
 * horizontal one — that one moves focus with Left/Right because a tab strip
 * is horizontal; this one uses Up/Down because the surfaces panel is
 * vertical. Ignores every other key (Escape, Tab) so it can bubble to
 * whatever owns closing/trapping.
 */
export function useRovingFocus(itemSelector: string): RovingFocus {
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;

    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(itemSelector));
    if (items.length === 0) return;
    const currentIndex = items.findIndex((el) => el === document.activeElement);

    e.preventDefault();

    if (e.key === 'Home') {
      items[0]?.focus();
      return;
    }
    if (e.key === 'End') {
      items[items.length - 1]?.focus();
      return;
    }
    if (currentIndex === -1) return;

    const nextIndex = e.key === 'ArrowDown'
      ? (currentIndex + 1) % items.length
      : (currentIndex - 1 + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return { onKeyDown };
}
